import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { AuthService } from '../../core/services/auth';
import { CLINICAL, DOSSIER_REGISTRY, type AppRole } from '../../core/permissions/role-groups';

/** Clés de l'ancien hub `/patient-dossier-hub`. */
export type LegacyDossierTab = 'dossiers' | 'chroniques' | 'carnets';

/** Anciens onglets Prescriptions / Examens / Imagerie du hub, devenus onglets du panneau de l'aperçu. */
const LEGACY_SECTIONS: Record<string, string> = { prescriptions: 'prescriptions', examens: 'exams', imageries: 'imaging' };

/**
 * Destination d'un ancien lien du hub « Dossiers patients ».
 *
 * - dossiers (registre)  → `/patients?view=apercu`
 * - chroniques           → `/patients?view=chronique`
 * - carnets              → `/e-carnets`
 *
 * Les paramètres déjà présents (`patientId`, `recordId`, `openCreate`…) sont conservés.
 * Sans onglet explicite, on retombe sur le premier onglet que le rôle voyait dans le
 * hub : registre pour l'accueil et les médecins, suivi chronique pour l'aide-soignante.
 */
export function legacyDossierTarget(
  router: Router,
  role: string | undefined,
  queryParams: Record<string, unknown>,
  forcedTab?: LegacyDossierTab,
): UrlTree {
  const { tab: requested, ...rest } = queryParams;
  const requestedKey = typeof requested === 'string' ? requested : '';
  const appRole = role as AppRole | undefined;
  const firstTab: LegacyDossierTab = appRole && DOSSIER_REGISTRY.includes(appRole)
    ? 'dossiers'
    : appRole && CLINICAL.includes(appRole) ? 'chroniques' : 'dossiers';
  const section = LEGACY_SECTIONS[requestedKey];
  const tab: LegacyDossierTab = forcedTab
    ?? (section ? 'dossiers'
      : requestedKey === 'chroniques' || requestedKey === 'carnets' || requestedKey === 'dossiers' ? requestedKey : firstTab);

  if (tab === 'carnets') return router.createUrlTree(['/e-carnets'], { queryParams: rest });
  if (tab === 'chroniques') return router.createUrlTree(['/patients'], { queryParams: { ...rest, view: 'chronique' } });
  return router.createUrlTree(['/patients'], { queryParams: { ...rest, view: 'apercu', ...(section ? { section } : {}) } });
}

/** `redirectTo` d'une ancienne route : conserve la query string, choisit la nouvelle vue. */
export const toPatientsView =
  (forcedTab?: LegacyDossierTab) =>
  ({ queryParams }: { queryParams: Record<string, unknown> }): UrlTree =>
    legacyDossierTarget(inject(Router), inject(AuthService).currentUser()?.role, queryParams, forcedTab);
